import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle2, GraduationCap } from 'lucide-react';
import { BRAND_NAME } from '../../brand';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicLeadCapture() {
  const { subdomain } = useParams();
  const [tenant, setTenant] = useState(null);
  const [courses, setCourses] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    guardian_name: '', student_level: '', source: 'website',
    school_class: '', school_board: '', school_name: '',
    college_year: '', college_name: '',
    professional_course: '', professional_level: '',
    competitive_exam: '',
    interested_courses: [], notes: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/tenants/subdomain/${subdomain}`);
        setTenant(res.data);
        try {
          const cRes = await axios.get(`${API}/api/public/courses/${subdomain}`);
          setCourses(cRes.data || []);
        } catch { /* courses optional */ }
      } catch {
        toast.error('Organization not found');
      }
    };
    load();
  }, [subdomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) {
      toast.error('Please provide either email or phone');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/crm/lead/${subdomain}`, form);
      setSubmitted(true);
      toast.success('Thank you! We will reach out to you soon.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-violet-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="lead-success">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold">Thank You!</h2>
            <p className="text-muted-foreground">
              We've received your enquiry. Our admissions team will contact you shortly.
            </p>
            <Button onClick={() => { setSubmitted(false); setForm({ ...form, first_name: '', last_name: '', email: '', phone: '', notes: '' }); }} variant="outline">
              Submit Another Enquiry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-violet-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <GraduationCap className="w-12 h-12 mx-auto text-primary mb-2" />
          <h1 className="text-3xl font-bold">{tenant?.name || subdomain}</h1>
          <p className="text-muted-foreground mt-1">Admission Enquiry Form</p>
        </div>

        <Card data-testid="lead-capture-form">
          <CardHeader><CardTitle>Tell us about yourself</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">First Name *</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="first-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Last Name *</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="last-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="email-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="phone-input" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">* Either email or phone is required</p>

              <div className="space-y-1">
                <Label className="text-xs">Guardian / Parent Name</Label>
                <Input value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">I am interested in</Label>
                <select className="form-select" value={form.student_level} onChange={(e) => setForm({ ...form, student_level: e.target.value })}>
                  <option value="">Select your level...</option>
                  <option value="school">School Education</option>
                  <option value="college">College / Inter</option>
                  <option value="competitive">Competitive Exam Prep</option>
                  <option value="professional">Professional Courses</option>
                </select>
              </div>

              {form.student_level === 'school' && (
                <div className="grid grid-cols-3 gap-2 p-3 border rounded-md bg-muted/30">
                  <select className="form-select-inline" value={form.school_class} onChange={(e) => setForm({ ...form, school_class: e.target.value })}>
                    <option value="">Class</option>{Array.from({length:12},(_,i)=>i+1).map(n=><option key={n} value={String(n)}>Class {n}</option>)}
                  </select>
                  <select className="form-select-inline" value={form.school_board} onChange={(e) => setForm({ ...form, school_board: e.target.value })}>
                    <option value="">Board</option><option value="CBSE">CBSE</option><option value="SSC">SSC</option><option value="ICSE">ICSE</option><option value="IGSE">IGSE</option><option value="Others">Others</option>
                  </select>
                  <Input placeholder="School Name" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} />
                </div>
              )}
              {form.student_level === 'college' && (
                <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-muted/30">
                  <select className="form-select-inline" value={form.college_year} onChange={(e) => setForm({ ...form, college_year: e.target.value })}>
                    <option value="">Year</option><option value="Inter 1st Year">Inter 1st Year</option><option value="Inter 2nd Year">Inter 2nd Year</option>
                  </select>
                  <Input placeholder="College Name" value={form.college_name} onChange={(e) => setForm({ ...form, college_name: e.target.value })} />
                </div>
              )}
              {form.student_level === 'competitive' && (
                <div className="p-3 border rounded-md bg-muted/30">
                  <select className="form-select" value={form.competitive_exam} onChange={(e) => setForm({ ...form, competitive_exam: e.target.value })}>
                    <option value="">Select Exam</option><option value="EAMCET">EAMCET</option><option value="MAINS">JEE Mains</option><option value="ADVANCED">JEE Advanced</option><option value="NEET">NEET</option>
                  </select>
                </div>
              )}
              {form.student_level === 'professional' && (
                <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-muted/30">
                  <select className="form-select-inline" value={form.professional_course} onChange={(e) => setForm({ ...form, professional_course: e.target.value })}>
                    <option value="">Course</option><option value="CA">CA</option><option value="CMA">CMA</option><option value="CS">CS</option><option value="US CMA">US CMA</option><option value="Others">Others</option>
                  </select>
                  <select className="form-select-inline" value={form.professional_level} onChange={(e) => setForm({ ...form, professional_level: e.target.value })}>
                    <option value="">Level</option><option value="Foundation">Foundation</option><option value="Inter">Inter</option><option value="Final">Final</option><option value="Part 1">Part 1</option><option value="Part 2">Part 2</option>
                  </select>
                </div>
              )}

              {courses.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Courses you're interested in</Label>
                  <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                    {courses.map(c => (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded">
                        <input type="checkbox" checked={form.interested_courses.includes(c.id)}
                          onChange={(e) => {
                            if (e.target.checked) setForm({ ...form, interested_courses: [...form.interested_courses, c.id] });
                            else setForm({ ...form, interested_courses: form.interested_courses.filter(id => id !== c.id) });
                          }} />
                        {c.title}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs">How did you hear about us?</Label>
                <select className="form-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="walk_in">Walk-in</option>
                  <option value="phone">Phone Call</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Additional Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Tell us anything else..." />
              </div>

              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-lead-btn">
                {submitting ? 'Submitting...' : 'Submit Enquiry'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Powered by <a href="/" className="font-medium hover:underline">{BRAND_NAME}</a>
        </p>
      </div>
    </div>
  );
}
